/**
 * The one place the stringly-typed party identity key (`${id_type}:${id_number}`) is built/parsed, and
 * the one person/organization fallback heuristic. Previously both were hand-threaded through
 * `useParties`, `PartyLinker.vue`, `clients/new.vue`, `parties/[id].vue`, and the server case join —
 * a drift risk (there is no canonical entity FK yet; that arrives with the C3.5 entity model).
 */
import { normalizeIdType, type PartyKind } from '../entities/vocabulary'

export { PARTY_KINDS, isPartyKind, type PartyKind } from '../entities/vocabulary'

/** Canonical identity key for an extracted party. */
export function partyKey(idType: string, idNumber: string): string {
  return `${idType}:${idNumber}`
}

/** Inverse of `partyKey` — splits on the FIRST colon (id numbers never contain one; id types don't either). */
export function parsePartyKey(key: string): { idType: string; idNumber: string } {
  const idx = key.indexOf(':')
  if (idx === -1) return { idType: key, idNumber: '' }
  return { idType: key.slice(0, idx), idNumber: key.slice(idx + 1) }
}

// Strong organization signals in a NAME: "persona jurídica" and common company-name suffixes.
// The id type is deliberately NOT matched here — see `inferPartyKind`.
const ORG_RE = /jur[íi]dica|sociedad|s\.?\s?a\.?(\s+de\s+c\.?\s?v\.?)?\b|\bs\.?\s?a\.?\s+de|ltda|\bcorp\b|\binc\b|\bllc\b|asociaci[óo]n|fundaci[óo]n|compañ[íi]a|\bcompany\b|\bs\.?\s?en\s?c\b/i

/**
 * Resolve a party's kind. **Always prefers the server's explicit `party_kind`**; the name/id heuristic
 * is only a fallback for documents not yet re-indexed (scheduled for removal once re-indexing completes).
 *
 * The id type used to be concatenated onto the name and matched by `ORG_RE`, whose `\bnit\b` branch
 * was the organization signal. Normalizing `NIT` → `tax_id` (MAR-129) would have silently killed
 * that, so the type is now read through the shared vocabulary instead of by regex: it answers *what
 * sort of identifier*, and only some answers imply *who*. `business_registration` is decisive; a
 * `tax_id` is not, because people hold tax ids too.
 */
export function inferPartyKind(name: string, idType?: string | null, explicit?: string | null): PartyKind {
  if (explicit === 'person' || explicit === 'organization') return explicit
  // The name is the stronger signal and is checked first: the corpus contains an *S.A. de C.V.*
  // filed with a personal id, and a company name outranks a mis-extracted id type.
  if (ORG_RE.test(name)) return 'organization'
  if (idType) {
    const normalized = normalizeIdType(idType)
    if (normalized.idType === 'business_registration') return 'organization'
    // A raw value can name the actor outright ("cédula de persona jurídica"); a canonical one
    // cannot — `tax_id` is held by people and companies alike, so it decides nothing.
    if (normalized.kind === 'organization') return 'organization'
  }
  return 'person'
}
